import { useCallback, useEffect, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { api } from '../lib/api';
import { formatCents } from '../lib/format';
import { readFileText } from '../lib/fileText';
import { PageHelp } from '../components/PageHelp';
import type { ImportCsvResult, MappingsOverview, UnassignedGroup } from '../lib/types';

type TabKey = 'kostenstellen' | 'konten' | 'gruppen' | 'kategorien' | 'kontoregeln' | 'offen';

const CSV_KIND: Partial<Record<TabKey, { kind: string; format: string }>> = {
  kostenstellen: { kind: 'costCentres', format: 'Kostenstelle;Bezeichnung;Abteilung;Bereich' },
  konten: { kind: 'accounts', format: 'Kontonummer;Bezeichnung;Art;Gruppe' },
  kategorien: { kind: 'categories', format: 'Kategorie;Kontonummer' },
};

/** FR-6/6a/15/18: Zuordnungstabellen pflegen, per CSV laden, Lücken sehen (nur Admin). */
export default function ZuordnungenPage() {
  const [tab, setTab] = useState<TabKey>('kostenstellen');
  const [data, setData] = useState<MappingsOverview>();
  const [error, setError] = useState<string>();
  const [result, setResult] = useState<ImportCsvResult>();
  const fileInput = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      setData(await api.get<MappingsOverview>('/admin/mappings'));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Zuordnungen konnten nicht geladen werden');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(action: () => Promise<unknown>) {
    setError(undefined);
    try {
      await action();
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen');
    }
  }

  async function uploadCsv(file: File) {
    const csv = CSV_KIND[tab];
    if (!csv) return;
    setResult(undefined);
    await run(async () => {
      setResult(
        await api.post<ImportCsvResult>('/admin/mappings/import', {
          kind: csv.kind,
          csv: await readFileText(file),
        }),
      );
    });
  }

  if (!data && !error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const csv = CSV_KIND[tab];
  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Typography variant="h4">Zuordnungen</Typography>
        <PageHelp text="Hier wird festgelegt, welcher Abteilung und welchem steuerlichen Bereich eine Buchung angehört (über die Kostenstelle) und auf welches Konto sie zählt (über die SevDesk-Kategorie). Änderungen wirken sofort auf alle bereits importierten Buchungen." />
      </Stack>
      <Tabs
        value={tab}
        onChange={(_, v: TabKey) => {
          setTab(v);
          setResult(undefined);
        }}
        variant="scrollable"
        allowScrollButtonsMobile
      >
        <Tab value="kostenstellen" label="Kostenstellen" />
        <Tab value="konten" label="Konten" />
        <Tab value="gruppen" label="Gruppen" />
        <Tab value="kategorien" label="Kategorien" />
        <Tab value="kontoregeln" label="Kontoregeln" />
        <Tab value="offen" label="Nicht zugeordnet" />
      </Tabs>
      {error && <Alert severity="error">{error}</Alert>}
      {csv && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{ alignItems: { sm: 'center' } }}
        >
          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            onClick={() => fileInput.current?.click()}
          >
            CSV importieren
          </Button>
          <Typography variant="body2" color="text.secondary">
            Format: <code>{csv.format}</code>
            {tab === 'konten' && ' -- ersetzt die bisherige Kontentabelle'}
          </Typography>
          <input
            ref={fileInput}
            type="file"
            accept=".csv,text/csv"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (file) void uploadCsv(file);
            }}
          />
        </Stack>
      )}
      {result && (
        <Alert
          severity={result.errors.length ? 'warning' : 'success'}
          onClose={() => setResult(undefined)}
        >
          {result.imported} Zeilen übernommen
          {result.removed ? `, ${result.removed} entfernt` : ''}
          {result.createdGroups?.length ? `, neue Gruppen: ${result.createdGroups.join(', ')}` : ''}
          {result.createdDivisions?.length
            ? `, neue Abteilungen: ${result.createdDivisions.join(', ')}`
            : ''}
          .
          {result.errors.map((e) => (
            <Typography key={e} variant="body2">
              {e}
            </Typography>
          ))}
        </Alert>
      )}
      {data && tab === 'kostenstellen' && <CostCentresTab data={data} run={run} />}
      {data && tab === 'konten' && <AccountsTab data={data} run={run} />}
      {data && tab === 'gruppen' && <GroupsTab data={data} run={run} />}
      {data && tab === 'kategorien' && <CategoriesTab data={data} run={run} />}
      {data && tab === 'kontoregeln' && <RulesTab data={data} run={run} />}
      {data && tab === 'offen' && <UnassignedTab data={data} run={run} />}
    </Stack>
  );
}

type Run = (action: () => Promise<unknown>) => Promise<void>;

/** D-24: nur hier kann Umsatzsteuer erlaubt sein -- im ideellen Bereich nie. */
const VAT_AREAS = ['WB', 'ZB', 'VV'];

function CostCentresTab({ data, run }: { data: MappingsOverview; run: Run }) {
  const divisionName = (id?: string) => data.divisions.find((d) => d.divisionId === id)?.name;
  const areaName = (key?: string) => data.taxAreas.find((t) => t.key === key)?.name;
  const save = (
    number: string,
    patch: { divisionId?: string; taxArea?: string; vatAllowed?: boolean },
  ) => {
    const cc = data.costCentres.find((c) => c.number === number)!;
    return run(() =>
      api.put(`/admin/mappings/cost-centres/${encodeURIComponent(number)}`, {
        divisionId: cc.divisionId ?? null,
        taxArea: cc.taxArea ?? null,
        ...patch,
      }),
    );
  };
  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Nr.</TableCell>
            <TableCell>Bezeichnung (SevDesk)</TableCell>
            <TableCell>Abteilung</TableCell>
            <TableCell>Bereich</TableCell>
            <TableCell>
              <Tooltip title="Umsatzsteuer darf nur auf Kostenstellen mit diesem Kennzeichen gebucht werden (Wirtschafts-, Zweckbetrieb oder Vermögensverwaltung; im ideellen Bereich nie); sonst bekommen die Admins eine Mail. Als Kleinunternehmer überall ausschalten.">
                <span>USt erlaubt</span>
              </Tooltip>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.costCentres.map((cc) => (
            <TableRow key={cc.number} hover>
              <TableCell>{cc.number}</TableCell>
              <TableCell>{cc.name ?? '—'}</TableCell>
              <TableCell>
                <TextField
                  select
                  size="small"
                  value={cc.divisionId ?? ''}
                  onChange={(e) =>
                    void save(cc.number, { divisionId: e.target.value || undefined })
                  }
                  sx={{ minWidth: 180 }}
                  error={!cc.derived.divisionId}
                >
                  <MenuItem value="">
                    <em>
                      {cc.derived.divisionId
                        ? `aus Kürzel: ${divisionName(cc.derived.divisionId)}`
                        : 'nicht zugeordnet'}
                    </em>
                  </MenuItem>
                  {data.divisions.map((d) => (
                    <MenuItem key={d.divisionId} value={d.divisionId}>
                      {d.name}
                    </MenuItem>
                  ))}
                </TextField>
              </TableCell>
              <TableCell>
                <TextField
                  select
                  size="small"
                  value={cc.taxArea ?? ''}
                  onChange={(e) => void save(cc.number, { taxArea: e.target.value || undefined })}
                  sx={{ minWidth: 180 }}
                >
                  <MenuItem value="">
                    <em>
                      {cc.derived.taxArea ? `aus Kürzel: ${areaName(cc.derived.taxArea)}` : '—'}
                    </em>
                  </MenuItem>
                  {data.taxAreas.map((t) => (
                    <MenuItem key={t.key} value={t.key}>
                      {t.name}
                    </MenuItem>
                  ))}
                </TextField>
              </TableCell>
              <TableCell>
                {VAT_AREAS.includes(cc.derived.taxArea?.toUpperCase() ?? '') ? (
                  <Switch
                    checked={Boolean(cc.vatAllowed)}
                    onChange={() => void save(cc.number, { vatAllowed: !cc.vatAllowed })}
                    slotProps={{ input: { 'aria-label': `Umsatzsteuer auf ${cc.number} erlaubt` } }}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    nie
                  </Typography>
                )}
              </TableCell>
            </TableRow>
          ))}
          {data.costCentres.length === 0 && (
            <TableRow>
              <TableCell colSpan={5}>
                <Typography color="text.secondary">
                  Noch keine Kostenstellen -- sie kommen mit dem nächsten Import aus SevDesk.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function AccountsTab({ data, run }: { data: MappingsOverview; run: Run }) {
  const [number, setNumber] = useState('');
  const [name, setName] = useState('');
  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <TextField
          size="small"
          label="Konto"
          value={number}
          onChange={(e) => setNumber(e.target.value)}
        />
        <TextField
          size="small"
          label="Bezeichnung"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Button
          variant="contained"
          disabled={!number.trim() || !name.trim()}
          onClick={() =>
            void run(async () => {
              await api.put(`/admin/mappings/accounts/${encodeURIComponent(number.trim())}`, {
                name,
              });
              setNumber('');
              setName('');
            })
          }
        >
          Hinzufügen
        </Button>
      </Stack>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Konto</TableCell>
              <TableCell>Bezeichnung</TableCell>
              <TableCell>Art</TableCell>
              <TableCell>Gruppe</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {data.accounts.map((a) => (
              <TableRow key={a.number} hover>
                <TableCell>{a.number}</TableCell>
                <TableCell>{a.name}</TableCell>
                <TableCell>
                  <TextField
                    select
                    size="small"
                    value={a.art ?? ''}
                    onChange={(e) =>
                      void run(() =>
                        api.put(`/admin/mappings/accounts/${a.number}`, {
                          name: a.name,
                          art: e.target.value || null,
                          groupId: a.groupId ?? null,
                        }),
                      )
                    }
                    sx={{ minWidth: 150 }}
                  >
                    <MenuItem value="">
                      <em>—</em>
                    </MenuItem>
                    <MenuItem value="anlage">Anlage</MenuItem>
                    <MenuItem value="abschreibung">Abschreibung</MenuItem>
                    <MenuItem value="umsatzsteuer">Umsatzsteuer</MenuItem>
                  </TextField>
                </TableCell>
                <TableCell>
                  <TextField
                    select
                    size="small"
                    value={a.groupId ?? ''}
                    onChange={(e) =>
                      void run(() =>
                        api.put(`/admin/mappings/accounts/${a.number}`, {
                          name: a.name,
                          art: a.art ?? null,
                          groupId: e.target.value || null,
                        }),
                      )
                    }
                    sx={{ minWidth: 180 }}
                  >
                    <MenuItem value="">
                      <em>Sonstiges</em>
                    </MenuItem>
                    {data.groups.map((g) => (
                      <MenuItem key={g.groupId} value={g.groupId}>
                        {g.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Konto entfernen">
                    <IconButton
                      size="small"
                      onClick={() => {
                        if (
                          window.confirm(
                            `Konto ${a.number} entfernen? Es zählt dann nicht mehr im Report.`,
                          )
                        ) {
                          void run(() => api.del(`/admin/mappings/accounts/${a.number}`));
                        }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}

/** D-11/FR-6: Report-Gruppen -- Name, Reihenfolge, Seite (Einnahme/Ausgabe). */
function GroupsTab({ data, run }: { data: MappingsOverview; run: Run }) {
  const [name, setName] = useState('');
  const count = (groupId: string) => data.accounts.filter((a) => a.groupId === groupId).length;
  const save = (g: { groupId: string; name: string; kind?: string; order?: number }) =>
    run(() =>
      api.put(`/admin/mappings/groups/${encodeURIComponent(g.groupId)}`, {
        name: g.name,
        kind: g.kind || null,
        order: g.order,
      }),
    );
  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Gruppen fassen Konten im Dashboard verständlich zusammen, z.B. Sportbetrieb,
        Veranstaltungen, Personal. Konten ohne Gruppe erscheinen unter "Sonstige
        Einnahmen/Ausgaben". Die Seite richtet sich ohne Angabe nach dem Konto (Klasse 4 =
        Einnahme). Zugeordnet wird im Reiter Konten oder per CSV-Spalte "Gruppe".
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <TextField
          size="small"
          label="Neue Gruppe"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Button
          variant="contained"
          disabled={!name.trim()}
          onClick={() =>
            void run(async () => {
              await api.put('/admin/mappings/groups/neu', { name, order: data.groups.length + 1 });
              setName('');
            })
          }
        >
          Anlegen
        </Button>
      </Stack>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Reihenfolge</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Seite</TableCell>
              <TableCell align="right">Konten</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {data.groups.map((g) => (
              <TableRow key={g.groupId} hover>
                <TableCell>
                  <TextField
                    size="small"
                    type="number"
                    defaultValue={g.order ?? ''}
                    onBlur={(e) => {
                      const order = Number(e.target.value);
                      if (order !== (g.order ?? 0)) void save({ ...g, order });
                    }}
                    sx={{ width: 90 }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    defaultValue={g.name}
                    onBlur={(e) => {
                      if (e.target.value.trim() && e.target.value !== g.name) {
                        void save({ ...g, name: e.target.value.trim() });
                      }
                    }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    select
                    size="small"
                    value={g.kind ?? ''}
                    onChange={(e) => void save({ ...g, kind: e.target.value })}
                    sx={{ minWidth: 150 }}
                  >
                    <MenuItem value="">
                      <em>nach Konto</em>
                    </MenuItem>
                    <MenuItem value="einnahme">Einnahme</MenuItem>
                    <MenuItem value="ausgabe">Ausgabe</MenuItem>
                  </TextField>
                </TableCell>
                <TableCell align="right">{count(g.groupId)}</TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    aria-label="Gruppe löschen"
                    onClick={() => {
                      if (
                        window.confirm(
                          `Gruppe "${g.name}" löschen? Ihre Konten fallen auf "Sonstiges" zurück.`,
                        )
                      ) {
                        void run(() =>
                          api.del(`/admin/mappings/groups/${encodeURIComponent(g.groupId)}`),
                        );
                      }
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {data.groups.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography color="text.secondary">Noch keine Gruppen.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}

function CategoriesTab({ data, run }: { data: MappingsOverview; run: Run }) {
  const [onlyOpen, setOnlyOpen] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const accountName = (n?: string) => data.accounts.find((a) => a.number === n)?.name;
  const save = (categoryId: string, accountNumber: string | null) =>
    run(() => api.put(`/admin/mappings/categories/${categoryId}`, { accountNumber }));
  const rows = data.categories.filter((c) => !onlyOpen || !c.effectiveAccount);
  return (
    <Stack spacing={1}>
      <FormControlLabel
        control={<Switch checked={onlyOpen} onChange={(e) => setOnlyOpen(e.target.checked)} />}
        label="Nur Kategorien ohne Konto"
      />
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Kategorie (SevDesk)</TableCell>
              <TableCell>SKR04 von SevDesk</TableCell>
              <TableCell>Konto (Verein)</TableCell>
              <TableCell>gilt</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((c) => (
              <TableRow key={c.categoryId} hover>
                <TableCell>{c.name}</TableCell>
                <TableCell>{c.skr04 ?? '—'}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <TextField
                      size="small"
                      value={drafts[c.categoryId] ?? c.accountNumber ?? ''}
                      onChange={(e) => setDrafts({ ...drafts, [c.categoryId]: e.target.value })}
                      onBlur={() => {
                        const v = drafts[c.categoryId];
                        if (v !== undefined && v !== (c.accountNumber ?? ''))
                          void save(c.categoryId, v.trim() || null);
                      }}
                      sx={{ width: 110 }}
                    />
                    {c.suggestion && (
                      <Chip
                        size="small"
                        label={`Vorschlag ${c.suggestion}`}
                        onClick={() => void save(c.categoryId, c.suggestion!)}
                      />
                    )}
                  </Stack>
                </TableCell>
                <TableCell>
                  {c.effectiveAccount ? (
                    `${c.effectiveAccount} ${accountName(c.effectiveAccount) ?? '(nicht im Report)'}`
                  ) : (
                    <Typography color="error" variant="body2">
                      kein Konto
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}

function RulesTab({ data, run }: { data: MappingsOverview; run: Run }) {
  const [account, setAccount] = useState('');
  const [costCentre, setCostCentre] = useState('');
  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Buchungen ohne Kostenstelle bekommen die Kostenstelle ihres Kontos, z.B. 6855
        (Zahlungsdifferenzen bei Kartenzahlung) → 720.
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <TextField
          size="small"
          label="Konto"
          value={account}
          onChange={(e) => setAccount(e.target.value)}
        />
        <TextField
          select
          size="small"
          label="Kostenstelle"
          value={costCentre}
          onChange={(e) => setCostCentre(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          {data.costCentres.map((cc) => (
            <MenuItem key={cc.number} value={cc.number}>
              {cc.number} {cc.name}
            </MenuItem>
          ))}
        </TextField>
        <Button
          variant="contained"
          disabled={!account.trim() || !costCentre}
          onClick={() =>
            void run(async () => {
              await api.put(`/admin/mappings/account-rules/${encodeURIComponent(account.trim())}`, {
                costCentreNumber: costCentre,
              });
              setAccount('');
              setCostCentre('');
            })
          }
        >
          Regel anlegen
        </Button>
      </Stack>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Konto</TableCell>
              <TableCell>Kostenstelle</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {data.accountRules.map((r) => (
              <TableRow key={r.accountNumber} hover>
                <TableCell>
                  {r.accountNumber} {data.accounts.find((a) => a.number === r.accountNumber)?.name}
                </TableCell>
                <TableCell>
                  {r.costCentreNumber}{' '}
                  {data.costCentres.find((c) => c.number === r.costCentreNumber)?.name}
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    aria-label="Regel löschen"
                    onClick={() =>
                      void run(() => api.del(`/admin/mappings/account-rules/${r.accountNumber}`))
                    }
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {data.accountRules.length === 0 && (
              <TableRow>
                <TableCell colSpan={3}>
                  <Typography color="text.secondary">Noch keine Kontoregeln.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography variant="h6">Belegregeln</Typography>
      <Typography variant="body2" color="text.secondary">
        Kostenstelle für einzelne, in SevDesk festgeschriebene Belege -- angelegt unter "Nicht
        zugeordnet".
      </Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Beleg</TableCell>
              <TableCell>Kostenstelle</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {data.voucherRules.map((r) => (
              <TableRow key={r.voucherId} hover>
                <TableCell>{r.voucherNumber ?? r.voucherId}</TableCell>
                <TableCell>
                  {r.costCentreNumber}{' '}
                  {data.costCentres.find((c) => c.number === r.costCentreNumber)?.name}
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    aria-label="Belegregel löschen"
                    onClick={() =>
                      void run(() =>
                        api.del(`/admin/mappings/voucher-rules/${encodeURIComponent(r.voucherId)}`),
                      )
                    }
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {data.voucherRules.length === 0 && (
              <TableRow>
                <TableCell colSpan={3}>
                  <Typography color="text.secondary">Noch keine Belegregeln.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}

function UnassignedTab({ data, run }: { data: MappingsOverview; run: Run }) {
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(String(thisYear));
  const [groups, setGroups] = useState<UnassignedGroup[]>();
  const [error, setError] = useState<string>();
  const [reload, setReload] = useState(0);

  useEffect(() => {
    setGroups(undefined);
    api
      .get<{ groups: UnassignedGroup[] }>(`/admin/mappings/unassigned?year=${year}`)
      .then((r) => setGroups(r.groups))
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : 'Laden fehlgeschlagen'),
      );
  }, [year, reload]);

  /** Festgeschriebene Belege ohne Kostenstelle: Kostenstelle je Beleg in MyBudget festlegen. */
  const withoutCostCentre = (groups ?? [])
    .filter((g) => g.problem === 'kostenstelle' && g.key === '')
    .flatMap((g) => g.examples);

  async function assign(
    voucherId: string,
    voucherNumber: string | undefined,
    costCentreNumber: string,
  ) {
    await run(() =>
      api.put(`/admin/mappings/voucher-rules/${encodeURIComponent(voucherId)}`, {
        costCentreNumber,
        voucherNumber,
      }),
    );
    setReload((n) => n + 1);
  }

  const total = (groups ?? []).reduce((sum, g) => sum + g.sumCents, 0);
  return (
    <Stack spacing={2}>
      <TextField
        select
        size="small"
        label="Geschäftsjahr"
        value={year}
        onChange={(e) => setYear(e.target.value)}
        sx={{ width: 160 }}
      >
        {Array.from({ length: thisYear - 2017 }, (_, i) => String(thisYear - i)).map((y) => (
          <MenuItem key={y} value={y}>
            {y}
          </MenuItem>
        ))}
      </TextField>
      {error && <Alert severity="error">{error}</Alert>}
      {!groups && !error && <CircularProgress />}
      {groups && groups.length === 0 && (
        <Alert severity="success">
          Alle Buchungen {year} sind einer Abteilung und einem Konto zugeordnet.
        </Alert>
      )}
      {groups && groups.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Ursache</TableCell>
                <TableCell>Was fehlt</TableCell>
                <TableCell align="right">Buchungen</TableCell>
                <TableCell align="right">Summe</TableCell>
                <TableCell>Beispiele</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map((g) => (
                <TableRow key={`${g.problem}|${g.key}`} hover>
                  <TableCell>
                    {g.problem === 'kategorie' ? 'Kategorie ohne Konto' : 'Keine Abteilung'}
                  </TableCell>
                  <TableCell>{g.label}</TableCell>
                  <TableCell align="right">{g.count}</TableCell>
                  <TableCell align="right">{formatCents(g.sumCents)}</TableCell>
                  <TableCell>
                    {g.examples
                      .slice(0, 5)
                      .map((e) => `${e.voucherNumber ?? '?'} (${e.stichtag})`)
                      .join(', ')}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={3}>
                  <strong>Summe nicht zugeordnet</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>{formatCents(total)}</strong>
                </TableCell>
                <TableCell />
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {withoutCostCentre.length > 0 && (
        <Stack spacing={1}>
          <Typography variant="h6">Belege ohne Kostenstelle</Typography>
          <Typography variant="body2" color="text.secondary">
            Am besten in SevDesk korrigieren. Ist der Beleg dort festgeschrieben, hier die
            Kostenstelle für genau diesen Beleg wählen -- sie gilt nur in MyBudget.
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Beleg</TableCell>
                  <TableCell>Datum</TableCell>
                  <TableCell align="right">Betrag</TableCell>
                  <TableCell>Text</TableCell>
                  <TableCell>Kostenstelle</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {withoutCostCentre.map((e) => (
                  <TableRow key={e.voucherId} hover>
                    <TableCell>{e.voucherNumber ?? '?'}</TableCell>
                    <TableCell>{e.stichtag}</TableCell>
                    <TableCell align="right">{formatCents(e.amountCents)}</TableCell>
                    <TableCell>{e.text ?? ''}</TableCell>
                    <TableCell>
                      <TextField
                        select
                        size="small"
                        value=""
                        label="wählen"
                        onChange={(ev) =>
                          void assign(e.voucherId, e.voucherNumber, ev.target.value)
                        }
                        sx={{ minWidth: 180 }}
                      >
                        {data.costCentres.map((cc) => (
                          <MenuItem key={cc.number} value={cc.number}>
                            {cc.number} {cc.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      )}
    </Stack>
  );
}
